import { createContext, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { Button, Flex, Space, Tooltip, Typography } from 'antd';
import { ColumnType } from 'antd/es/table';
import { useTranslation } from 'react-i18next';
import { FanguardApiContractsUserUserDetailModel, FanguardApiContractsUserUserSourceEnum } from 'src/api/generated';
import { useGlobal } from 'src/contexts/GlobalContext';
import { useDeleteUser, useGetUsers, useResetUserPassword, useUnlockUser } from 'src/hooks/api/useUsersEndpoint';
import { useModal } from 'src/hooks/useModal';
import { usePagination } from 'src/hooks/usePagination';
import { useAppNavigation } from 'src/routes';

import ExternalLink from '@/assets/icons/ExternalLink.svg';
import { EditOutlined, LockOutlined } from '@ant-design/icons';

import UsersMobileRow from './components/UsersTable/components/UsersMobileRow';
import {
  ResetPasswordDescription,
  ResetPasswordWrapper,
  StyledDeleteOutlined,
  StyledSpace,
} from './components/UsersTable/UsersTable.styled';
import { StyledChangePassword } from './UsersContext.styled';
import { useAuth } from 'src/contexts/AuthContext';

interface UsersContextProps {
  isLoading: boolean;
  users: FanguardApiContractsUserUserDetailModel[];
  searchTerm: string;
  tableColumns: ColumnType<FanguardApiContractsUserUserDetailModel>[];
  page: number;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  totalItems: number;
  filterBySearch: (value: string) => void;
  onPageChange: (pageNumber: number) => void;
}

export const UsersContext = createContext<UsersContextProps | undefined>(undefined);

export const UsersProvider = ({ children }: { children: ReactNode }) => {
  const { t } = useTranslation();
  const { isDesktop } = useGlobal();
  const { navigateTo } = useAppNavigation();
  const { confirm, contextHolder } = useModal();
  const { mutateAsync } = useDeleteUser();
  const { mutateAsync: resetUserPassword } = useResetUserPassword();
  const { mutateAsync: unlockUser } = useUnlockUser({ invalidateAll: true });
  const [activeUsername, setActiveUsername] = useState<string | null>(null);
  const [userResettingPassword, setUserResettingPassword] = useState<string | null>(null);
  const [userUnlocking, setUserUnlocking] = useState<string | null>(null);
  const { currentUser: user } = useAuth();
  const [searchTerm, setSearchTerm] = useState('');
  const { page, pageSize, setPage, setPageSize } = usePagination();

  const { data: users, isLoading } = useGetUsers({
    page,
    pageSize,
    searchTerm,
  });

  const onPageChange = useCallback((pageNumber: number) => setPage(pageNumber), []);

  const filterBySearch = useCallback((value: string) => setSearchTerm(value), []);

  const onUnlockUser = async (d: FanguardApiContractsUserUserDetailModel) => {
    if (d.username) {
      setUserUnlocking(d.username);
      await unlockUser(d.username);
      setUserUnlocking(null);
    }
  };

  const onEditUser = (d: FanguardApiContractsUserUserDetailModel) => navigateTo.usersDetails(d.username);

  const onDeleteUser = (d: FanguardApiContractsUserUserDetailModel) => {
    confirm({
      title: `${t('general.delete')} ${t('users.user')}?`,
      onOk: async () => {
        if (d.username) {
          await mutateAsync(d.username);
          navigateTo.users();
        }
      },
    });
  };

  const handleClickResetPassword = (d: FanguardApiContractsUserUserDetailModel) => {
    confirm({
      title: `${t('users.table.resetPassword')}?`,
      content: (
        <ResetPasswordWrapper>
          <ResetPasswordDescription $isDesktop>
            {t('users.table.resetPasswordTitle', { username: d.username, email: d.email })}
          </ResetPasswordDescription>
          <ResetPasswordDescription $isDesktop>
            {t('users.table.resetPasswordDescription', { username: d.username, email: d.email })}
          </ResetPasswordDescription>
        </ResetPasswordWrapper>
      ),
      onOk: async () => {
        await onResetUserPassword(d);
      },
    });
  };

  const onResetUserPassword = async (d: FanguardApiContractsUserUserDetailModel) => {
    if (d.username) {
      setUserResettingPassword(d.username);
      await resetUserPassword(d.username);
      setUserResettingPassword(null);
    }
  };

  const desktopColumns: ColumnType<FanguardApiContractsUserUserDetailModel>[] = useMemo(
    () => [
      {
        title: '',
        dataIndex: 'goToUser',
        key: 'goToUser',
        width: 56,
        render: (_value, record) => {
          return record.source === FanguardApiContractsUserUserSourceEnum.Local ? null : (
            <Tooltip title={t('users.table.externalUser')}>
              <Flex align="center" justify="center">
                <ExternalLink />
              </Flex>
            </Tooltip>
          );
        },
      },
      {
        title: t('users.table.firstName'),
        dataIndex: 'firstName',
        key: 'firstName',
        width: 132,
      },
      {
        title: t('users.table.lastName'),
        dataIndex: 'lastName',
        key: 'lastName',
        width: 132,
      },
      {
        title: t('users.table.emailAddress'),
        dataIndex: 'email',
        key: 'email',
        render: (value) => (
          <Tooltip title={value}>
            <Typography.Text ellipsis>{value}</Typography.Text>
          </Tooltip>
        ),
      },
      {
        title: t('users.table.username'),
        dataIndex: 'username',
        key: 'username',
      },
      {
        title: t('users.table.actions'),
        dataIndex: 'actions',
        key: 'actions',
        width: 178,
        render: (_, record) => (
          <StyledSpace size={8}>
            {record.lockedOut ? (
              <Tooltip title={t('users.table.lockUser')}>
                <Button
                  type="text"
                  icon={<LockOutlined />}
                  onClick={() => onUnlockUser(record)}
                  loading={userUnlocking === record.username}
                />
              </Tooltip>
            ) : (
              <Space size={8}></Space>
            )}
            <Tooltip title={t('users.table.resetPassword')}>
              <Button
                type="text"
                icon={<StyledChangePassword />}
                onClick={() => handleClickResetPassword(record)}
                loading={userResettingPassword === record.username}
              />
            </Tooltip>
            <Tooltip title={t('users.table.editUser')}>
              <Button type="text" icon={<EditOutlined />} onClick={() => onEditUser(record)} />
            </Tooltip>
            <Tooltip title={t('users.table.deleteUser')}>
              <Button onClick={() => onDeleteUser(record)} type="text" icon={<StyledDeleteOutlined />} />
            </Tooltip>
          </StyledSpace>
        ),
      },
    ],
    [userResettingPassword, userUnlocking, user],
  );

  const mobileColumns: ColumnType<FanguardApiContractsUserUserDetailModel>[] = useMemo(
    () => [
      {
        title: t('users.table.firstName'),
        dataIndex: 'firstName',
        key: 'firstName',
        render: (_value, record) => (
          <UsersMobileRow
            users={users?.items ?? []}
            user={record}
            activeUsername={activeUsername}
            setActiveUsername={setActiveUsername}
            showResetPassword={record.source === FanguardApiContractsUserUserSourceEnum.Local}
            onEditUser={(username) => navigateTo.usersDetails(username)}
            onAfterDelete={() => navigateTo.users()}
          />
        ),
      },
    ],
    [users, activeUsername, setActiveUsername],
  );

  const tableColumns = isDesktop ? desktopColumns : mobileColumns;

  const contextValue = useMemo(
    () => ({
      isLoading: isLoading,
      users: users?.items ?? [],
      searchTerm,
      tableColumns,
      page,
      pageSize,
      setPageSize,
      totalItems: users?.totalItems ?? 0,
      filterBySearch,
      onPageChange,
    }),
    [isLoading, users, filterBySearch, searchTerm, tableColumns, page, pageSize, setPageSize],
  );

  return (
    <UsersContext.Provider value={contextValue}>
      {contextHolder}
      {children}
    </UsersContext.Provider>
  );
};

export const useUsers = () => {
  const context = useContext(UsersContext);
  if (!context) {
    throw new Error('useUsers must be used within a UsersProvider or AdminUsersProvider');
  }
  return context;
};
